import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BondType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { AssignBeneficiaryDto, Bond } from 'service/dto/bond.dto';
import {
  AssignTrusteeDto,
  BeneficiariesAllocationDto
} from 'service/dto/general.dto';

export const useCreateBond = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (bondType: BondType): Promise<void> => {
      return services.bondService.createBond(bondType);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-bond']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetBondList = () =>
  useQuery(['bonds'], () => services.bondService.getBonds(), {
    refetchOnWindowFocus: false
  });

export const useDeleteBond = () =>
  useMutation((bondId: number) => services.bondService.deleteBond(bondId), {
    onError: (error: Error) => {
      toast.error(<Alert message={error.message} type="error" />);
    }
  });

export const useAssignBondBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (assignBeneficiaryDto: AssignBeneficiaryDto): Promise<void> => {
      return services.bondService.assignBeneficiary(assignBeneficiaryDto);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-bond-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetBondDetails = ({ bondId }: { bondId: number }) => {
  return useQuery<Bond, AxiosError<Error>>(
    ['get_bond', bondId],
    () => services.bondService.getBond(bondId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!bondId
    }
  );
};

export const useUnAssignBondBeneficiary = () =>
  useMutation(
    ({ allocationId }: { allocationId?: number }) =>
      services.bondService.unAssignBondBeneficiary(allocationId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useBondAllocationBulkEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiariesAllocationDto: BeneficiariesAllocationDto): Promise<void> => {
      return services.bondService.editBulkBondBeneficiary(
        beneficiariesAllocationDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bond-allocation-edit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useRemoveBondTrustee = () =>
  useMutation(
    ({ assetId, trusteeId }: { assetId: number; trusteeId: number }) =>
      services.bondService.removeBondTrustee(assetId, trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignBondTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ assetId, trusteeId, note }: AssignTrusteeDto): Promise<void> => {
      return services.bondService.assignBondTrustee(assetId, trusteeId, note);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-bond-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
